import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, generateId } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';
import { Plus, ChevronLeft, ChevronRight, Pencil, Trash2, Copy, ArrowRight } from 'lucide-react';
import type { Budget } from '@/lib/types';

export default function BudgetPage() {
  const { data, updateData } = useAuth();
  const budgets = data.budgets ?? [];
  const categories = data.categories ?? [];

  // Month navigation
  const [monthOffset, setMonthOffset] = useState(0);
  const viewDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + monthOffset);
    return d;
  }, [monthOffset]);
  const viewMonth = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}`;
  const monthLabel = viewDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [limitAmount, setLimitAmount] = useState('');

  const budgetData = useMemo(() => {
    const monthBudgets = budgets.filter(b => b.month === viewMonth);
    const monthExpenses = data.transactions.filter(t => t.type === 'expense' && t.date.startsWith(viewMonth));

    return monthBudgets.map(budget => {
      const spent = monthExpenses.filter(t => t.category === budget.category).reduce((s, t) => s + t.amount, 0);
      const pct = budget.monthlyLimit > 0 ? (spent / budget.monthlyLimit) * 100 : 0;
      return { ...budget, spent, pct };
    });
  }, [budgets, data.transactions, viewMonth]);

  const totalBudget = budgetData.reduce((s, b) => s + b.monthlyLimit, 0);
  const totalSpent = budgetData.reduce((s, b) => s + b.spent, 0);

  // Categories not yet budgeted this month
  const usedCategories = budgetData.map(b => b.category);
  const availableCategories = categories.filter(c => !usedCategories.includes(c));

  const openAdd = () => {
    setEditingBudget(null);
    setSelectedCategory(availableCategories[0] ?? '');
    setLimitAmount('');
    setDialogOpen(true);
  };

  const openEdit = (b: Budget) => {
    setEditingBudget(b);
    setSelectedCategory(b.category);
    setLimitAmount(String(b.monthlyLimit));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const amount = parseFloat(limitAmount);
    if (!selectedCategory || isNaN(amount) || amount <= 0) return;

    if (editingBudget) {
      await updateData(prev => ({
        ...prev,
        budgets: (prev.budgets ?? []).map(b =>
          b.id === editingBudget.id ? { ...b, category: selectedCategory, monthlyLimit: amount } : b
        ),
      }));
    } else {
      const newBudget: Budget = {
        id: generateId(),
        category: selectedCategory,
        monthlyLimit: amount,
        month: viewMonth,
      };
      await updateData(prev => ({
        ...prev,
        budgets: [...(prev.budgets ?? []), newBudget],
      }));
    }
    setDialogOpen(false);
  };

  const handleDelete = async (id: string) => {
    await updateData(prev => ({
      ...prev,
      budgets: (prev.budgets ?? []).filter(b => b.id !== id),
    }));
  };

  const copyFromPreviousMonth = async () => {
    const prevDate = new Date(viewDate);
    prevDate.setMonth(prevDate.getMonth() - 1);
    const prevMonth = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
    const prevBudgets = budgets.filter(b => b.month === prevMonth);
    if (prevBudgets.length === 0) return;

    const newBudgets: Budget[] = prevBudgets
      .filter(pb => !usedCategories.includes(pb.category))
      .map(pb => ({
        id: generateId(),
        category: pb.category,
        monthlyLimit: pb.monthlyLimit,
        month: viewMonth,
      }));

    if (newBudgets.length === 0) return;

    await updateData(prev => ({
      ...prev,
      budgets: [...(prev.budgets ?? []), ...newBudgets],
    }));
  };

  // Check if previous month has budgets to copy
  const prevDate = new Date(viewDate);
  prevDate.setMonth(prevDate.getMonth() - 1);
  const prevMonth = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
  const hasPrevBudgets = budgets.some(b => b.month === prevMonth);

  // Check next month for "copy forward"
  const nextDate = new Date(viewDate);
  nextDate.setMonth(nextDate.getMonth() + 1);
  const nextMonth = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
  const nextMonthHasBudgets = budgets.some(b => b.month === nextMonth);

  const copyToNextMonth = async () => {
    if (budgetData.length === 0) return;
    const existingNext = budgets.filter(b => b.month === nextMonth).map(b => b.category);
    const newBudgets: Budget[] = budgetData
      .filter(b => !existingNext.includes(b.category))
      .map(b => ({ id: generateId(), category: b.category, monthlyLimit: b.monthlyLimit, month: nextMonth }));
    if (newBudgets.length === 0) return;
    await updateData(prev => ({ ...prev, budgets: [...(prev.budgets ?? []), ...newBudgets] }));
  };

  return (
    <div className="px-4 pt-6 pb-24">
      <h1 className="mb-4 font-display text-2xl font-bold">Budget</h1>

      {/* Month navigation */}
      <div className="mb-4 flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => setMonthOffset(o => o - 1)}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="text-center">
          <p className="font-medium">{monthLabel}</p>
          {monthOffset !== 0 && (
            <button className="text-xs text-primary" onClick={() => setMonthOffset(0)}>
              Back to current
            </button>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMonthOffset(o => o + 1)}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Summary card */}
      {budgetData.length > 0 && (
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total spent</span>
              <span className="font-semibold">{formatAUD(totalSpent)} / {formatAUD(totalBudget)}</span>
            </div>
            <Progress
              value={totalBudget > 0 ? Math.min((totalSpent / totalBudget) * 100, 100) : 0}
              className="mt-2 h-2"
            />
          </CardContent>
        </Card>
      )}

      {/* Action buttons */}
      <div className="mb-4 flex gap-2">
        <Button onClick={openAdd} disabled={availableCategories.length === 0} className="flex-1">
          <Plus className="mr-1 h-4 w-4" /> Add Budget
        </Button>
        {hasPrevBudgets && budgetData.length === 0 && (
          <Button variant="outline" onClick={copyFromPreviousMonth} className="flex-1">
            <Copy className="mr-1 h-4 w-4" /> Copy Last Month
          </Button>
        )}
        {budgetData.length > 0 && !nextMonthHasBudgets && (
          <Button variant="outline" onClick={copyToNextMonth} className="flex-1">
            <ArrowRight className="mr-1 h-4 w-4" /> Copy to Next Month
          </Button>
        )}
      </div>

      {/* Budget list */}
      {budgetData.length === 0 ? (
        <div className="mt-8 text-center">
          <p className="text-muted-foreground">No budgets set for {monthLabel}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Tap "Add Budget" to set spending limits by category
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {budgetData.map(b => (
            <Card key={b.id}>
              <CardContent className="p-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="font-medium">{b.category}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">
                      {formatAUD(b.spent)} / {formatAUD(b.monthlyLimit)}
                    </p>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(b)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete budget?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Remove the {b.category} budget for {monthLabel}?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(b.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <Progress
                  value={Math.min(b.pct, 100)}
                  className={cn(
                    'h-2',
                    b.pct > 100 ? '[&>div]:bg-destructive' : b.pct > 80 ? '[&>div]:bg-warning' : '[&>div]:bg-success'
                  )}
                />
                {b.pct > 100 && (
                  <p className="mt-1 text-xs text-destructive">Over budget by {formatAUD(b.spent - b.monthlyLimit)}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBudget ? 'Edit Budget' : 'Add Budget'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Category</label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory} disabled={!!editingBudget}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {(editingBudget ? [editingBudget.category] : availableCategories).map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Monthly Limit ($)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={limitAmount}
                onChange={e => setLimitAmount(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!selectedCategory || !limitAmount || parseFloat(limitAmount) <= 0}>
              {editingBudget ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
